"use client";

import { useLocale } from "next-intl";
import { usePostHog } from "posthog-js/react";
import { Link, usePathname } from "@/i18n/navigation";
import { cn } from "@/lib/utils";
import { useSiteFlavor } from "@/components/layout/site-flavor-provider";

/** Capsule, active pill and idle label per world — the admin header is the only
 *  place this component sits on the violet glass rather than the zinc chrome. */
const SKINS = {
  default: {
    capsule: "border border-line bg-surface-dim",
    active: "bg-surface-mute text-ink",
    idle: "text-ink-mute hover:text-ink",
  },
  admin: {
    capsule: "admin-glass",
    active: "bg-admin-accent/14 text-admin-accent-soft",
    idle: "text-white/48 hover:text-white",
  },
} as const;

export function LangSwitcher({
  variant = "default",
}: {
  variant?: keyof typeof SKINS;
}) {
  const locale = useLocale();
  const pathname = usePathname();
  const posthog = usePostHog();
  const { isGetmint } = useSiteFlavor();
  const skin = SKINS[variant];
  if (isGetmint) return null;

  return (
    <div className={cn("flex rounded-full p-0.5", skin.capsule)}>
      <Link
        href={pathname}
        locale="ru"
        onClick={() => posthog?.capture("lang_switched", { from: locale, to: "ru" })}
        className={cn(
          "rounded-full px-3 py-1 text-xs font-medium transition-colors",
          locale === "ru" ? skin.active : skin.idle
        )}
      >
        RU
      </Link>
      <Link
        href={pathname}
        locale="en"
        onClick={() => posthog?.capture("lang_switched", { from: locale, to: "en" })}
        className={cn(
          "rounded-full px-3 py-1 text-xs font-medium transition-colors",
          locale === "en" ? skin.active : skin.idle
        )}
      >
        EN
      </Link>
    </div>
  );
}
